import { setupIssue, validationIssue } from "./issues";
import { validateComboChart } from "../logic/chartCombo";
import { conditionBlankValue, isDateLikeColumn, nullFallbackIssue } from "../logic/sqlExpressions";
import type { ReportValidationIssue, ReportValidationContext } from "./issues";
import type {
    CategorizationRule,
    ExtraBreakdown,
    ExcelDerivedRowDef,
    MetricGroup,
    MetricSection,
    OtpCondition,
    OtpRule,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
    ReportFolder,
    ReportSubTab,
    RuleCondition,
    RuleNode,
    RuleSet,
    SectionExcelLayout,
} from "../types";
import {
    MAX_RULE_GROUP_DEPTH,
    buildLateConditionSql,
    breakdownColumnForModel,
    categorizationRuleIssues,
    activeDataModels,
    detailColumnsForModel,
    findSelectedDetailColumn,
    DETAIL_RULE_SET_ALIAS_MAX_LENGTH,
    detailRuleSetAliasText,
    detailRuleSetOutputs,
    selectedDetailRuleSetIds,
    usesDetailRuleSetList,
    columnsForModel,
    dataColumnSelectionKey,
    excelSectionAliases,
    findReportColumn,
    isCustomReportingPeriod,
    isLastNReportingPeriod,
    lastNLimit,
    validLastN,
    isCategorizationRuleReady,
    isRuleGroupNode,
    isRuleSetReady,
    metricFilterIssues,
    metricFilterNodes,
    isPeriodDateExtra,
    periodDateIssues,
    periodBoundsIssues,
    metricOverridesReportPeriod,
    metricPeriodFilterIssues,
    metricPeriodFilterExpr,
    normalizeReportingPeriodType,
    reconcileExcelLayoutColumns,
    validateExcelSheetName,
    excelSheetsInUse,
    resolveCountDistinctColumn,
    ruleLeafConditions,
    ruleOpDef,
    ruleResultValue,
    ruleValueSourceIssue,
    calculatedFields,
    calculatedFieldIssues,
    detailCalculatedColumns,
    detailConstantIssues,
    measureIssue,
    usesCombinedRuleSets,
    combinedRuleSetIds,
    combinedRuleSetIssues,
    calculatedColumnReferenceIssue,
    isCalculatedColumnKey,
    detailAggregateIssues,
} from "../reportConfigurationLogic";

const trim = (value: unknown) => String(value ?? "").trim();

const isKnownOption = (options: ReportConfigurationLookupOption[], value: string) => {
    if (!options.length) return true;
    const normalized = value.trim().toLowerCase();
    return options.some((option) =>
        String(option.value).trim().toLowerCase() === normalized
        || String(option.name).trim().toLowerCase() === normalized);
};

const parseDateValue = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
        return null;
    }
    return date.getTime();
};

const duplicateValues = (values: string[]) => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    values.forEach((value) => {
        const normalized = trim(value).toLowerCase();
        if (!normalized) return;
        if (seen.has(normalized)) duplicates.add(trim(value));
        seen.add(normalized);
    });
    return Array.from(duplicates);
};

const hasBlankValue = (values: string[]) => values.some((value) => !trim(value));

/**
 * Typed values of a calculated field used as a column (include / exclude lists, value
 * groups): a number or date field can only match values of its own kind - the server binds
 * them as numbers / dates and would otherwise fail the query. Null when they all fit.
 */
function calculatedValuesIssue(report: ReportConfig, modelKey: string, columnKey: string, values: string[]) {
    if (!isCalculatedColumnKey(columnKey) || !values.length) return null;
    const column = findReportColumn({ ...report, models: modelKey ? [modelKey] : report.models }, columnKey);
    if (!column) return null;
    const category = String(column.dataTypeCategory || "");
    const bad = values.filter((value) => {
        const text = trim(value);
        if (!text) return false;
        if (category === "numeric") return !/^-?\d+(\.\d+)?$/.test(text);
        if (category === "date") return parseDateValue(text.slice(0, 10)) === null;
        return false;
    });
    if (!bad.length) return null;
    return category === "numeric"
        ? `"${column.label}" is a number, so these values can never match: ${bad.join(", ")}. Use numbers.`
        : `"${column.label}" is a date, so these values can never match: ${bad.join(", ")}. Use dates (YYYY-MM-DD).`;
}

function validateDateOrder(
    issues: ReportValidationIssue[],
    start: string,
    end: string,
    idPrefix: string,
    title: string,
    startFieldKey: string,
    endFieldKey: string,
) {
    if (start && parseDateValue(start) === null) {
        issues.push(setupIssue(`${idPrefix}.start.invalid`, title, "Start date must be a valid date.", startFieldKey));
    }
    if (end && parseDateValue(end) === null) {
        issues.push(setupIssue(`${idPrefix}.end.invalid`, title, "End date must be a valid date.", endFieldKey));
    }

    const startDate = start ? parseDateValue(start) : null;
    const endDate = end ? parseDateValue(end) : null;
    if (startDate !== null && endDate !== null && endDate < startDate) {
        issues.push(setupIssue(`${idPrefix}.order`, title, "End date must be on or after the start date.", endFieldKey));
    }
}

const hasText = (value: unknown) => trim(value).length > 0;

const conditionValues = (condition: RuleCondition) =>
    (condition.values || []).map((value) => trim(value)).filter(Boolean);

function addRuleConditionValueIssue(
    issues: ReportValidationIssue[],
    tab: ReportSubTab,
    idPrefix: string,
    title: string,
    condition: RuleCondition,
    fieldKey: string,
    metadata: Partial<Pick<ReportValidationIssue, "sectionId" | "ruleSetId" | "ruleId">>,
) {
    // Column comparisons use compareColumn; time differences still need typed bounds.
    if (condition.compareTo === "column" && condition.leftKind !== "minutes_between") return;
    // "Compare To Today" has no typed value: the run date is the right-hand side.
    if (condition.compareTo === "today" && condition.leftKind !== "minutes_between") return;

    const operator = ruleOpDef(condition.operator);
    const values = conditionValues(condition);

    if (operator.valueMode === "none") return;

    if (operator.valueMode === "text") {
        if (!hasText(condition.text)) {
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.text`, title, `"${operator.label}" needs text to match.`, fieldKey, metadata));
        }
        return;
    }

    if (operator.valueMode === "range") {
        if (!hasText(condition.values?.[0]) || !hasText(condition.values?.[1])) {
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.between`, title, `"${operator.label}" requires both From and To values.`, fieldKey, metadata));
        }
        return;
    }

    if (!values.length) {
        issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.value`, title, `"${operator.label}" needs at least one value.`, fieldKey, metadata));
    }
}

function validateRuleNodes(
    issues: ReportValidationIssue[],
    tab: ReportSubTab,
    idPrefix: string,
    title: string,
    report: ReportConfig,
    nodes: RuleNode[],
    depth: number,
    fieldKey: string,
    metadata: Partial<Pick<ReportValidationIssue, "sectionId" | "ruleSetId" | "ruleId">>,
) {
    nodes.forEach((node) => {
        if (isRuleGroupNode(node)) {
            if (!node.conditions.length) {
                issues.push(validationIssue(tab, `${idPrefix}.group.${node.id}.empty`, title, "A condition group is empty. Add a condition inside it, or delete the group.", fieldKey, metadata));
            } else if (!ruleLeafConditions(node.conditions).length) {
                issues.push(validationIssue(tab, `${idPrefix}.group.${node.id}.noLeaves`, title, "A condition group only contains empty groups. Add a condition, or delete the group.", fieldKey, metadata));
            }
            if (depth + 1 > MAX_RULE_GROUP_DEPTH) {
                issues.push(validationIssue(tab, `${idPrefix}.group.${node.id}.tooDeep`, title, `A condition group is nested deeper than ${MAX_RULE_GROUP_DEPTH} levels. Flatten the logic before saving.`, fieldKey, metadata));
            }
            validateRuleNodes(issues, tab, idPrefix, title, report, node.conditions || [], depth + 1, fieldKey, metadata);
            return;
        }

        const condition = node as RuleCondition;
        if (!hasText(condition.column)) {
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.column`, title, "A condition is missing its column.", fieldKey, metadata));
            return;
        }

        if (!findReportColumn(report, condition.column)) {
            const calcIssue = calculatedColumnReferenceIssue(report, report.models.length === 1 ? report.models[0] : "", condition.column);
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.columnMissing`, title, calcIssue ? `A condition: ${calcIssue}` : "A condition uses a column that is not available in the selected data models.", fieldKey, metadata));
        }

        if (!hasText(condition.operator)) {
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.operator`, title, "A condition is missing its operator.", fieldKey, metadata));
            return;
        }

        // Date operands: "Compare To Today" needs a date column.
        const column = findReportColumn(report, condition.column);
        if (condition.compareTo === "today" && condition.leftKind !== "minutes_between" && column && !isDateLikeColumn(column)) {
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.todayNotDate`, title, `"${column.label}" does not hold dates, so it cannot be compared to today.`, fieldKey, metadata));
        }
        // "If blank, use": the value must fit the column's own type - the server refuses the SQL otherwise.
        const fallbackIssue = conditionBlankValue(condition) !== undefined && column ? nullFallbackIssue(condition, column) : null;
        if (fallbackIssue) {
            issues.push(validationIssue(tab, `${idPrefix}.condition.${condition.id}.blankValue`, title, fallbackIssue, fieldKey, metadata));
        }

        addRuleConditionValueIssue(issues, tab, idPrefix, title, condition, fieldKey, metadata);
    });
}

function validateCategorizationRule(
    issues: ReportValidationIssue[],
    report: ReportConfig,
    ruleSet: RuleSet,
    rule: CategorizationRule,
) {
    const title = `${ruleSet.name || "Untitled Rule Set"} / ${rule.label || "Rule"}`;
    const fieldKey = `cat-rule-${ruleSet.id}-${rule.id}`;
    const metadata = { ruleSetId: ruleSet.id, ruleId: rule.id };
    const idPrefix = `ruleset.${ruleSet.id}.rule.${rule.id}`;

    if (!rule.conditions.length || !ruleLeafConditions(rule.conditions).length) {
        issues.push(validationIssue("categorization", `${idPrefix}.conditions.required`, title, "Add at least one condition to this rule.", fieldKey, metadata));
    }

    validateRuleNodes(issues, "categorization", idPrefix, title, report, rule.conditions || [], 0, fieldKey, metadata);

    if (rule.action === "set") {
        if (!hasText(rule.targetField)) {
            issues.push(validationIssue("categorization", `${idPrefix}.target.required`, title, "Choose the target field this rule sets.", fieldKey, metadata));
        }
        if (rule.valueSource === "column") {
            // Nothing chosen, gone from the catalog, not allowed, or on another model.
            const sourceIssue = ruleValueSourceIssue(report, rule);
            if (sourceIssue) {
                issues.push(validationIssue("categorization", `${idPrefix}.result.column`, title, sourceIssue, fieldKey, metadata));
            }
        } else if (!ruleResultValue(rule)) {
            issues.push(validationIssue("categorization", `${idPrefix}.result.required`, title, "Enter or select the value this rule should set.", fieldKey, metadata));
        }
    }

    if (!isCategorizationRuleReady(report, rule)) {
        const advisoryIssues = categorizationRuleIssues(report, rule);
        // The value-source problem, when there is one, was already reported above as .result.column.
        if (advisoryIssues.length && !issues.some((issue) => issue.id === `categorization.${idPrefix}.ready` || issue.id === `categorization.${idPrefix}.result.column`)) {
            issues.push(validationIssue("categorization", `${idPrefix}.ready`, title, advisoryIssues[0].message, fieldKey, metadata));
        }
    }
}

function validateCategorization(
    issues: ReportValidationIssue[],
    report: ReportConfig,
) {
    const usedRuleSetIds = new Set(report.sections.flatMap((section) => {
        if (usesDetailRuleSetList(section)) return selectedDetailRuleSetIds(section);
        if (usesCombinedRuleSets(section)) return combinedRuleSetIds(section);
        return section.breakdownSource === "ruleSet" && section.ruleSetId ? [section.ruleSetId] : [];
    }));

    report.bucketRuleSets.forEach((ruleSet) => {
        const fieldKey = `ruleset-${ruleSet.id}`;
        const metadata = { ruleSetId: ruleSet.id };
        const ruleSetTitle = ruleSet.name || "Untitled Rule Set";

        if (!hasText(ruleSet.name)) {
            issues.push(validationIssue("categorization", `ruleset.${ruleSet.id}.name.required`, "Rule set name", "Enter a name for this categorization rule set.", fieldKey, metadata));
        }

        if (usedRuleSetIds.has(ruleSet.id) && !isRuleSetReady(report, ruleSet)) {
            issues.push(validationIssue("categorization", `ruleset.${ruleSet.id}.used.notReady`, ruleSetTitle, "This rule set is used by a metric but has no ready rule output or include/exclude rule.", fieldKey, metadata));
        }

        if (usedRuleSetIds.has(ruleSet.id) && !ruleSet.rules.length) {
            issues.push(validationIssue("categorization", `ruleset.${ruleSet.id}.rules.required`, ruleSetTitle, "This rule set is used by a metric and needs at least one rule.", fieldKey, metadata));
        }

        ruleSet.rules.forEach((rule) => validateCategorizationRule(issues, report, ruleSet, rule));
    });
}

function validateMetricGroups(
    issues: ReportValidationIssue[],
    section: MetricSection,
    groups: MetricGroup[],
    idPrefix: string,
    titlePrefix: string,
    fieldKeyPrefix: string,
) {
    groups.forEach((group) => {
        const fieldKey = `${fieldKeyPrefix}-${group.id}`;
        const metadata = { sectionId: section.id };
        const title = `${section.name || "Untitled Metric"} / ${titlePrefix}`;
        if (!hasText(group.name)) {
            issues.push(validationIssue("metrics", `${idPrefix}.group.${group.id}.name.required`, title, "Enter a name for this value group.", fieldKey, metadata));
        }
        if (!group.values.length) {
            issues.push(validationIssue("metrics", `${idPrefix}.group.${group.id}.values.required`, title, "Add at least one value to this group, or delete the empty group.", fieldKey, metadata));
        }
    });
}

function validateExtraBreakdowns(issues: ReportValidationIssue[], report: ReportConfig, section: MetricSection) {
    section.extraBreakdowns.forEach((extra: ExtraBreakdown) => {
        const fieldKey = `metric-extra-${section.id}-${extra.id}`;
        const title = `${section.name || "Untitled Metric"} / Extra Breakdown`;
        const metadata = { sectionId: section.id };
        // A Period Date part has no column of its own - it is checked by validatePeriodDate.
        if (isPeriodDateExtra(extra)) {
            if (!section.periodDate) {
                issues.push(validationIssue("metrics", `metric.${section.id}.extra.${extra.id}.periodDate.missing`, title, "This breakdown groups by the Period Date, but the metric has no Period Date. Add one in Period Date, or remove this breakdown.", `metric-period-${section.id}`, metadata));
            }
            return;
        }
        if (!hasText(extra.column)) {
            issues.push(validationIssue("metrics", `metric.${section.id}.extra.${extra.id}.column.required`, title, "Choose the extra breakdown column, or remove this breakdown.", fieldKey, metadata));
        } else if (!breakdownColumnForModel(section.model, extra.column)) {
            const calcIssue = calculatedColumnReferenceIssue(report, section.model, extra.column);
            issues.push(validationIssue("metrics", `metric.${section.id}.extra.${extra.id}.column.missing`, title, calcIssue || "This extra breakdown column is no longer available for the selected model.", fieldKey, metadata));
        } else {
            const valuesIssue = calculatedValuesIssue(report, section.model, extra.column, extra.groups.flatMap((group) => group.values));
            if (valuesIssue) issues.push(validationIssue("metrics", `metric.${section.id}.extra.${extra.id}.values`, title, valuesIssue, fieldKey, metadata));
        }
        validateMetricGroups(issues, section, extra.groups, `metric.${section.id}.extra.${extra.id}`, "Extra Breakdown Group", `metric-extra-group-${section.id}-${extra.id}`);
    });
}

function validateOtpCondition(issues: ReportValidationIssue[], section: MetricSection, rule: OtpRule, condition: OtpCondition) {
    const fieldKey = `metric-otp-${section.id}`;
    const title = `${section.name || "Untitled Metric"} / OTP Rule`;
    const metadata = { sectionId: section.id };
    if (!hasText(condition.column)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.otp.${rule.id}.condition.${condition.id}.column.required`, title, "An OTP condition is missing its column.", fieldKey, metadata));
        return;
    }
    if (!columnsForModel(section.model).some((column) => column.key === condition.column)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.otp.${rule.id}.condition.${condition.id}.column.missing`, title, "An OTP condition uses a column that is not available for this model.", fieldKey, metadata));
    }
    if (!condition.values.length) {
        issues.push(validationIssue("metrics", `metric.${section.id}.otp.${rule.id}.condition.${condition.id}.values.required`, title, "An OTP condition needs at least one value.", fieldKey, metadata));
    }
}

function validateOtpRules(issues: ReportValidationIssue[], section: MetricSection) {
    if (section.displayAs !== "otp") return;

    const fieldKey = `metric-otp-${section.id}`;
    const title = `${section.name || "Untitled Metric"} / OTP Rules`;
    const metadata = { sectionId: section.id };

    if (!section.otpRules.length) {
        issues.push(validationIssue("metrics", `metric.${section.id}.otp.rules.required`, title, "Add at least one late-arrival rule for this OTP metric.", fieldKey, metadata));
        return;
    }

    section.otpRules.forEach((rule) => {
        if (!hasText(rule.compareCol)) {
            issues.push(validationIssue("metrics", `metric.${section.id}.otp.${rule.id}.compare.required`, title, "Choose the timestamp column to compare.", fieldKey, metadata));
        }
        if (!hasText(rule.againstCol)) {
            issues.push(validationIssue("metrics", `metric.${section.id}.otp.${rule.id}.against.required`, title, "Choose the timestamp column to compare against.", fieldKey, metadata));
        }
        if (!Number.isFinite(rule.thresholdMinutes) || rule.thresholdMinutes <= 0) {
            issues.push(validationIssue("metrics", `metric.${section.id}.otp.${rule.id}.threshold.required`, title, "Enter an OTP threshold greater than 0 minutes.", fieldKey, metadata));
        }
        rule.conditions.forEach((condition) => validateOtpCondition(issues, section, rule, condition));
    });

    if (!buildLateConditionSql(section)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.otp.notReady`, title, "Complete at least one OTP rule so the late-arrival condition can be generated.", fieldKey, metadata));
    }
}

function validateMetricFilters(issues: ReportValidationIssue[], report: ReportConfig, section: MetricSection) {
    (["filter", "excludeFilter"] as const).forEach((mode) => {
        const nodes = metricFilterNodes(section, mode);
        if (!nodes.length) return;

        const fieldKey = `metric-filter-${section.id}-${mode}`;
        const title = `${section.name || "Untitled Metric"} / ${mode === "filter" ? "Inclusion Criteria" : "Exclusion Criteria"}`;
        const metadata = { sectionId: section.id };

        validateRuleNodes(
            issues,
            "metrics",
            `metric.${section.id}.${mode}`,
            title,
            { ...report, models: section.model ? [section.model] : [] },
            nodes,
            0,
            fieldKey,
            metadata,
        );

        metricFilterIssues(report, section, mode).forEach((issue) => {
            if (issue.kind === "badOperator") {
                issues.push(validationIssue("metrics", `metric.${section.id}.${mode}.${issue.nodeId}.badOperator`, title, issue.message, fieldKey, metadata));
            }
        });

        if (metricFilterIssues(report, section, mode).some((issue) => issue.kind === "emptyGroup")) {
            issues.push(validationIssue("metrics", `metric.${section.id}.${mode}.empty`, title, "Complete at least one usable filter condition, or turn this filter off.", fieldKey, metadata));
        }
    });
}

function validatePeriodDate(issues: ReportValidationIssue[], report: ReportConfig, section: MetricSection) {
    if (!section.periodDate) return;
    const fieldKey = `metric-period-${section.id}`;
    const title = `${section.name || "Untitled Metric"} / Period Date`;
    const metadata = { sectionId: section.id };
    periodDateIssues(report, section).forEach((issue, index) => {
        issues.push(validationIssue("metrics", `metric.${section.id}.periodDate.${issue.nodeId}.${index}`, title, issue.message, fieldKey, metadata));
    });
}

// Per-metric reporting period override. Mirrors ReportSqlBuilder.MetricPeriodFilterClauses:
// an empty tree means "no date filter" and is allowed; a non-empty tree that produces no SQL
// is refused by the server, so it blocks the save here.
function validateMetricPeriodFilter(issues: ReportValidationIssue[], report: ReportConfig, section: MetricSection) {
    if (!metricOverridesReportPeriod(section)) return;
    const fieldKey = `metric-period-filter-${section.id}`;
    const title = `${section.name || "Untitled Metric"} / Reporting Period`;
    const metadata = { sectionId: section.id };
    metricPeriodFilterIssues(report, section).forEach((issue, index) => {
        issues.push(validationIssue("metrics", `metric.${section.id}.periodFilter.${issue.nodeId}.${index}`, title, issue.message, fieldKey, metadata));
    });
    if ((section.periodFilter?.conditions || []).length && !metricPeriodFilterExpr(report, section)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.periodFilter.incomplete`, title, "This metric's own date filter has no complete condition yet. Finish a condition, or remove them all to apply no date filter.", fieldKey, metadata));
    }
}

// SQL Server cannot compare these types, so SELECT DISTINCT fails outright when one is
// selected. Mirrors DistinctIncompatibleTypes in ReportSqlBuilder.
const DISTINCT_INCOMPATIBLE_TYPES = new Set(["text", "ntext", "image", "xml", "geography", "geometry", "hierarchyid", "sql_variant"]);

// Full Row Detail "Unique rows only" (detailDistinct).
function validateDetailDistinct(
    issues: ReportValidationIssue[],
    section: MetricSection,
    title: string,
    metadata: { sectionId: string },
) {
    const fieldKey = `metric-detail-distinct-${section.id}`;
    const allColumns = detailColumnsForModel(section.model);
    const selected = section.selectedColumns.length
        ? section.selectedColumns.map((key) => findSelectedDetailColumn(allColumns, key)).filter((column): column is NonNullable<typeof column> => !!column)
        : allColumns;

    selected
        .filter((column) => DISTINCT_INCOMPATIBLE_TYPES.has(String(column.dataType || "").trim().toLowerCase().replace(/\(.*$/, "")))
        .forEach((column) => {
            issues.push(validationIssue("metrics", `metric.${section.id}.detailDistinct.type.${dataColumnSelectionKey(column)}`, title, `"${column.label}" (${column.dataType}) can't be compared for unique rows. Remove it from the selected columns or turn off "Unique rows only".`, fieldKey, metadata));
        });

    if (!section.selectedColumns.length) {
        issues.push(validationIssue("metrics", `metric.${section.id}.detailDistinct.allColumns`, title, "Unique rows only is on, but no columns are chosen, so every visible column is compared. Rows are only removed when they match in all of them.", fieldKey, metadata, "warning"));
    }

    const recordKey = String(activeDataModels()[section.model]?.recordKeyColumn || "").trim().toLowerCase();
    const keyColumn = recordKey ? selected.find((column) => String(column.sql || "").trim().toLowerCase() === recordKey || String(column.key || "").trim().toLowerCase() === recordKey) : undefined;
    if (keyColumn) {
        issues.push(validationIssue("metrics", `metric.${section.id}.detailDistinct.recordKey`, title, `Unique rows only has no effect while "${keyColumn.label}" is selected - it is different on every row.`, fieldKey, metadata, "warning"));
    }
}

// Full Row Detail with several categorization rule sets (detailRuleSetIds). Mirrors the
// checks ReportSqlBuilder makes before it builds the detail query.
function validateDetailRuleSets(
    issues: ReportValidationIssue[],
    report: ReportConfig,
    section: MetricSection,
    title: string,
    fieldKey: string,
    metadata: { sectionId: string },
) {
    const ids = selectedDetailRuleSetIds(section);
    const seen = new Set<string>();
    ids.forEach((id) => {
        if (seen.has(id)) {
            issues.push(validationIssue("metrics", `metric.${section.id}.detailRuleSets.${id}.duplicate`, title, "The same categorization rule set is selected more than once.", fieldKey, metadata));
        }
        seen.add(id);
    });

    const modelScope: ReportConfig = { ...report, models: section.model ? [section.model] : [] };
    detailRuleSetOutputs(report, section).forEach((output) => {
        const issueMetadata = { ...metadata, ruleSetId: output.ruleSetId };
        const idPrefix = `metric.${section.id}.detailRuleSets.${output.ruleSetId}`;
        if (!output.ruleSet) {
            issues.push(validationIssue("metrics", `${idPrefix}.missing`, title, "A selected categorization rule set no longer exists. Remove it from this metric.", fieldKey, metadata));
            return;
        }
        const name = output.label;
        if (!isRuleSetReady(report, output.ruleSet)) {
            issues.push(validationIssue("metrics", `${idPrefix}.notReady`, title, `Rule set "${name}" is not complete enough to generate SQL.`, fieldKey, issueMetadata));
            return;
        }

        // A condition on a column this metric's data model doesn't have would be dropped from
        // the query and silently widen the rule, so it blocks the save instead.
        const foreignColumns = new Set<string>();
        output.ruleSet.rules.forEach((rule) => ruleLeafConditions(rule.conditions).forEach((condition) => {
            [condition.column, condition.compareColumn].forEach((columnKey) => {
                if (columnKey && !findReportColumn(modelScope, columnKey)) foreignColumns.add(columnKey);
            });
        }));
        if (foreignColumns.size) {
            issues.push(validationIssue("metrics", `${idPrefix}.foreignColumns`, title, `Rule set "${name}" uses columns that are not in this metric's data model (${Array.from(foreignColumns).join(", ")}).`, fieldKey, issueMetadata));
        }

        if (!output.columnExpr) {
            issues.push(validationIssue("metrics", `${idPrefix}.noColumn`, title, `Rule set "${output.ruleSet.name || name}" has no complete "Set" rules, so it adds no column - it only filters rows.`, fieldKey, issueMetadata, "warning"));
        }

        if (detailRuleSetAliasText(section, output.ruleSetId).trim().length > DETAIL_RULE_SET_ALIAS_MAX_LENGTH) {
            issues.push(validationIssue("metrics", `${idPrefix}.alias.length`, title, `The column name for rule set "${output.ruleSet.name}" must be ${DETAIL_RULE_SET_ALIAS_MAX_LENGTH} characters or fewer.`, fieldKey, issueMetadata));
        }
    });

    // Two categorization columns with the same name would be ambiguous in the query and on
    // the sheet. Compared case-insensitively, as SQL Server compares column names.
    const byName = new Map<string, string[]>();
    detailRuleSetOutputs(report, section).filter((output) => output.columnExpr).forEach((output) => {
        const key = output.label.trim().toLowerCase();
        byName.set(key, [...(byName.get(key) || []), output.label]);
    });
    byName.forEach((names, key) => {
        if (names.length > 1) {
            issues.push(validationIssue("metrics", `metric.${section.id}.detailRuleSets.alias.duplicate.${key}`, title, `Two categorization columns are both named "${names[0]}". Give each one a different column name.`, fieldKey, metadata));
        }
    });
}

function validateMetricSection(
    issues: ReportValidationIssue[],
    report: ReportConfig,
    section: MetricSection,
) {
    const fieldKey = `metric-${section.id}`;
    const title = section.name || "Untitled Metric";
    const metadata = { sectionId: section.id };

    if (!hasText(section.model)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.model.required`, title, "Choose a data model for this metric.", fieldKey, metadata));
    }

    if (!hasText(section.breakdownColumn)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.breakdown.required`, title, "Choose the column this metric counts by.", fieldKey, metadata));
    } else if (!breakdownColumnForModel(section.model, section.breakdownColumn)) {
        const calcIssue = calculatedColumnReferenceIssue(report, section.model, section.breakdownColumn);
        issues.push(validationIssue("metrics", `metric.${section.id}.breakdown.missing`, title, calcIssue ? `Breakdown: ${calcIssue}` : "The selected breakdown column is no longer available for this metric's model.", fieldKey, metadata));
    } else {
        const valuesIssue = calculatedValuesIssue(report, section.model, section.breakdownColumn, [...section.include, ...section.exclude, ...section.groups.flatMap((group) => group.values)]);
        if (valuesIssue) issues.push(validationIssue("metrics", `metric.${section.id}.breakdown.values`, title, valuesIssue, fieldKey, metadata));
    }

    // A calculated-field measure replaces the count, so the distinct column no longer applies.
    if (section.displayAs !== "detail" && !section.measure?.fieldId && section.countMode === "distinct" && !resolveCountDistinctColumn(section.model, section.countMode, section.countDistinctColumn)) {
        issues.push(validationIssue("metrics", `metric.${section.id}.distinct.required`, title, "Distinct count is selected. Choose the distinct column before saving.", `metric-count-${section.id}`, metadata));
    }

    if (section.displayAs === "detail" && section.detailDistinct) {
        validateDetailDistinct(issues, section, title, metadata);
    }

    if (usesDetailRuleSetList(section)) {
        validateDetailRuleSets(issues, report, section, title, fieldKey, metadata);
    } else if (usesCombinedRuleSets(section)) {
        // Combined rule sets - the same checks the editor shows inline and the server makes
        // before it builds the query (logic/combinedRuleSets.ts combinedRuleSetIssues).
        combinedRuleSetIssues(report, section).forEach((issue) => {
            issues.push(validationIssue(
                "metrics",
                `metric.${section.id}.combined.${issue.key}`,
                title,
                issue.message,
                issue.rowId
                    ? issue.key.endsWith(".distinct") ? `metric-combined-count-${section.id}-${issue.rowId}` : `metric-combined-row-${section.id}-${issue.rowId}`
                    : `metric-combined-${section.id}`,
                issue.ruleSetId ? { ...metadata, ruleSetId: issue.ruleSetId } : metadata,
                issue.severity,
            ));
        });
    } else if (section.breakdownSource === "ruleSet") {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
        if (!section.ruleSetId) {
            issues.push(validationIssue("metrics", `metric.${section.id}.ruleset.required`, title, "Choose the categorization rule set this metric should use.", fieldKey, metadata));
        } else if (!ruleSet) {
            issues.push(validationIssue("metrics", `metric.${section.id}.ruleset.missing`, title, "The selected categorization rule set no longer exists.", fieldKey, metadata));
        } else if (!isRuleSetReady(report, ruleSet)) {
            issues.push(validationIssue("metrics", `metric.${section.id}.ruleset.notReady`, title, "The selected categorization rule set is not complete enough to generate metric SQL.", fieldKey, { ...metadata, ruleSetId: ruleSet.id }));
        }
    }

    const overlap = usesCombinedRuleSets(section) ? [] : section.include.filter((value) => section.exclude.includes(value));
    if (overlap.length) {
        issues.push(validationIssue("metrics", `metric.${section.id}.includeExclude.overlap`, title, `These values are both included and excluded: ${overlap.join(", ")}.`, fieldKey, metadata));
    }

    const sectionMeasureIssue = measureIssue(report, section);
    if (sectionMeasureIssue) {
        issues.push(validationIssue("metrics", `metric.${section.id}.measure`, title, sectionMeasureIssue, `metric-measure-${section.id}`, metadata));
    }
    detailCalculatedColumns(report, section).forEach((item) => {
        if (item.expr) return;
        const message = !item.field
            ? "A calculated column on this metric no longer exists. Remove it from the metric."
            : item.field.model !== section.model
                ? `"${item.label}" is calculated on a different data model than this metric. Remove it from the metric.`
                : `"${item.label}" is not complete yet. Finish it on the Calculated Fields tab.`;
        issues.push(validationIssue("metrics", `metric.${section.id}.calc.${item.fieldId}`, title, message, `metric-detail-calc-${section.id}`, metadata));
    });

    // Aggregated sheet columns change the query even while the block is off the workbook.
    detailAggregateIssues(report, section).forEach((issue) => {
        issues.push(validationIssue("excel", `excel.${section.id}.detail.aggregate.${issue.sourceAlias}`, `${title} / Excel Layout`, issue.message, `excel-detail-columns-${section.id}`, metadata));
    });

    detailConstantIssues(section).forEach((message, index) => {
        issues.push(validationIssue("metrics", `metric.${section.id}.const.${index}`, title, message, `metric-detail-const-${section.id}`, metadata));
    });

    validateMetricGroups(issues, section, section.groups, `metric.${section.id}`, "Value Group", `metric-group-${section.id}`);
    validateExtraBreakdowns(issues, report, section);
    validatePeriodDate(issues, report, section);
    validateMetricPeriodFilter(issues, report, section);
    validateMetricFilters(issues, report, section);
    validateOtpRules(issues, section);
}

function validateCalculatedFields(issues: ReportValidationIssue[], report: ReportConfig) {
    calculatedFields(report).forEach((field) => {
        const title = (field.name || "").trim() || "Untitled Calculated Field";
        calculatedFieldIssues(report, field).forEach((message, index) => {
            issues.push(validationIssue("calculated", `calc.${field.id}.${index}`, title, message, `calc-field-${field.id}`));
        });
    });
}

function validateMetrics(issues: ReportValidationIssue[], report: ReportConfig) {
    if (!report.sections.length) {
        issues.push(validationIssue("metrics", "metrics.required", "Metrics", "Add at least one metric before saving this report configuration.", "metrics-list"));
        return;
    }

    report.sections.forEach((section) => validateMetricSection(issues, report, section));
}

const normalizeName = (value: unknown) => trim(value).toLowerCase();

function validateDerivedRows(
    issues: ReportValidationIssue[],
    section: MetricSection,
    rows: ExcelDerivedRowDef[],
) {
    const fieldKey = `excel-derived-${section.id}`;
    const title = `${section.name || "Untitled Metric"} / Derived Rows`;
    const metadata = { sectionId: section.id };

    rows.forEach((row) => {
        const rowLabel = row.label || "Derived row";
        const sourceRows = (row.sourceRowLabels || []).map((value) => trim(value)).filter(Boolean);

        if (!hasText(row.label)) {
            issues.push(validationIssue("excel", `excel.${section.id}.derived.${row.id}.label.required`, title, "Enter a label for each derived row.", fieldKey, metadata));
        }

        if (row.kind === "constant") {
            if (!Number.isFinite(row.constantValue)) {
                issues.push(validationIssue("excel", `excel.${section.id}.derived.${row.id}.constant.required`, rowLabel, "Enter a numeric value for this constant derived row.", fieldKey, metadata));
            }
            return;
        }

        if (row.kind === "ratio" || row.kind === "difference") {
            if (sourceRows.length !== 2) {
                issues.push(validationIssue("excel", `excel.${section.id}.derived.${row.id}.sourceRows.count`, rowLabel, "Choose exactly two source rows for this derived row.", fieldKey, metadata));
            }
            return;
        }

        if (row.kind === "remainderOf") {
            if (sourceRows.length < 2) {
                issues.push(validationIssue("excel", `excel.${section.id}.derived.${row.id}.sourceRows.count`, rowLabel, "Choose a total row and at least one row to subtract.", fieldKey, metadata));
            }
            return;
        }

        if (!sourceRows.length) {
            issues.push(validationIssue("excel", `excel.${section.id}.derived.${row.id}.sourceRows.required`, rowLabel, "Choose at least one source row for this derived row.", fieldKey, metadata));
        }
    });
}

function validateExcelTemplateRequirements(
    issues: ReportValidationIssue[],
    section: MetricSection,
    layout: SectionExcelLayout,
) {
    if (!layout.saveAsTemplate) return;

    const fieldKey = `excel-template-${section.id}`;
    const title = `${section.name || "Untitled Metric"} / Template`;
    const metadata = { sectionId: section.id };
    const templateName = trim(layout.templateName);

    if (!templateName) {
        issues.push(validationIssue("excel", `excel.${section.id}.template.name.required`, title, "Enter a template name before saving this layout as a template.", fieldKey, metadata));
    }

    if (layout.templateId && layout.templateSaveMode === "update" && layout.sourceTemplateIsGlobal) {
        issues.push(validationIssue("excel", `excel.${section.id}.template.globalUpdate`, title, "Global templates can only be updated from the Global Templates tab.", fieldKey, metadata));
    }

    if (layout.templateId && layout.templateSaveMode !== "update" && normalizeName(templateName) === normalizeName(layout.sourceTemplateName || "")) {
        issues.push(validationIssue("excel", `excel.${section.id}.template.rename.required`, title, "Change the template name before saving this selected template as a new report-level template.", fieldKey, metadata));
    }
}

function validateEnabledExcelLayout(
    issues: ReportValidationIssue[],
    report: ReportConfig,
    section: MetricSection,
    layout: SectionExcelLayout,
) {
    const metadata = { sectionId: section.id };
    const title = `${section.name || "Untitled Metric"} / Excel Layout`;
    const columnsFieldKey = `excel-columns-${section.id}`;

    if (!section.model || !section.breakdownColumn) {
        issues.push(validationIssue("excel", `excel.${section.id}.metric.notReady`, title, "Complete the metric model and breakdown before including this block in the workbook.", `excel-metric-${section.id}`, metadata));
        return;
    }

    const aliases = excelSectionAliases(section, report);
    if (!aliases.length || !layout.columns.length) {
        issues.push(validationIssue("excel", `excel.${section.id}.columns.required`, title, "This workbook block has no generated columns to place on the sheet.", columnsFieldKey, metadata));
        return;
    }

    if (section.displayAs === "detail") {
        const included = layout.columns.filter((column) => column.detail?.include);
        if (!included.length) {
            issues.push(validationIssue("excel", `excel.${section.id}.detail.columns.required`, title, "Include at least one detail column in this workbook block.", `excel-detail-columns-${section.id}`, metadata));
        }

        if (!section.selectedColumns.length) {
            issues.push(validationIssue(
                "excel",
                `excel.${section.id}.detail.selectedColumns.implicit`,
                title,
                "No specific detail columns are selected on the Metrics tab, so the workbook will use every visible column for this data model.",
                `excel-detail-columns-${section.id}`,
                metadata,
                "warning",
            ));
        }

        const headerCounts = new Map<string, number>();
        included.forEach((column) => {
            const header = trim(column.detail?.header || column.label);
            if (!header) return;
            headerCounts.set(header, (headerCounts.get(header) || 0) + 1);
        });
        headerCounts.forEach((count, header) => {
            if (count > 1) {
                issues.push(validationIssue(
                    "excel",
                    `excel.${section.id}.detail.header.${header}.duplicate`,
                    title,
                    `"${header}" is used as the header for ${count} included columns. Use unique headers if the workbook needs to distinguish them clearly.`,
                    `excel-detail-columns-${section.id}`,
                    metadata,
                    "warning",
                ));
            }
        });

        included.forEach((column) => {
            if (column.detail?.widthMode !== "fixed") return;
            const width = column.detail.width ?? 0;
            if (!Number.isFinite(width) || width < 1 || width > 255) {
                issues.push(validationIssue("excel", `excel.${section.id}.detail.${column.sourceAlias}.width.invalid`, column.detail.header || column.label, "Fixed column widths must be between 1 and 255.", `excel-detail-columns-${section.id}`, metadata));
            }
        });
        return;
    }

    const measures = layout.columns.filter((column) => column.role === "measure");
    const rowLabels = layout.columns.filter((column) => column.role === "rowLabel");
    const columnAxes = layout.columns.filter((column) => column.role === "columnAxis");

    if (measures.length !== 1) {
        issues.push(validationIssue("excel", `excel.${section.id}.measure.count`, title, `Choose exactly one workbook column as Value. Found ${measures.length}.`, columnsFieldKey, metadata));
    }
    if (!rowLabels.length) {
        issues.push(validationIssue("excel", `excel.${section.id}.rowLabel.required`, title, "Choose at least one workbook column as Row Label.", columnsFieldKey, metadata));
    }
    if (columnAxes.length > 1) {
        issues.push(validationIssue("excel", `excel.${section.id}.timeAxis.count`, title, "Only one workbook column can be the Time Axis.", columnsFieldKey, metadata));
    }

    measures.forEach((column) => {
        if (!column.measure) {
            issues.push(validationIssue("excel", `excel.${section.id}.measure.${column.sourceAlias}.options.required`, column.label, "Complete the Value formatting options for this workbook column.", columnsFieldKey, metadata));
            return;
        }
        if ((section.displayAs === "percentage" || section.displayAs === "otp") && column.measure.scale !== "divide100") {
            issues.push(validationIssue("excel", `excel.${section.id}.measure.${column.sourceAlias}.scale.required`, column.label, "Percentage and OTP workbook values must use Divide by 100 scaling.", columnsFieldKey, metadata));
        }
    });

    validateDerivedRows(issues, section, layout.derivedRows || []);

    // Combo chart rules (mirrors ReportChartConfigurationValidator on the server). Blocking,
    // but only while combo mode is on - see validateComboChart.
    validateComboChart(layout.chart).forEach((message, index) => {
        issues.push(validationIssue("excel", `excel.${section.id}.chart.combo.${index}`, `${section.name || "Untitled Metric"} / Chart`, message, `excel-chart-${section.id}`, metadata));
    });
}

function validateExcelLayouts(issues: ReportValidationIssue[], report: ReportConfig) {
    report.sections.forEach((section) => {
        if (!section.excelLayout) return;

        const layout = reconcileExcelLayoutColumns(section, section.excelLayout, report);
        const sheetProblem = layout.sheetName ? validateExcelSheetName(layout.sheetName) : null;
        if (sheetProblem) {
            issues.push(validationIssue("excel", `excel.${section.id}.sheet.invalid`, `${section.name || "Untitled Metric"} / Sheet`, sheetProblem, `excel-metric-${section.id}`, { sectionId: section.id }));
        }
        validateExcelTemplateRequirements(issues, section, layout);

        if (layout.enabled) {
            validateEnabledExcelLayout(issues, report, section, layout);
        }
    });

    excelSheetsInUse(report).forEach((sheet) => {
        const orderCounts = new Map<number, MetricSection[]>();
        sheet.metrics
            .filter((metric) => metric.enabled)
            .forEach((metric) => {
                const order = metric.section.excelLayout?.blockOrder ?? 10;
                orderCounts.set(order, [...(orderCounts.get(order) || []), metric.section]);
            });
        orderCounts.forEach((sections, order) => {
            if (sections.length > 1) {
                issues.push(validationIssue(
                    "excel",
                    `excel.sheet.${sheet.key}.blockOrder.${order}.duplicate`,
                    `${sheet.name} / Block Order`,
                    `Multiple included metrics use Block Order ${order}: ${sections.map((section) => section.name || "Untitled Metric").join(", ")}.`,
                    `excel-metric-${sections[0].id}`,
                    { sectionId: sections[0].id },
                    "warning",
                ));
            }
        });
    });
}

export function validateReportConfiguration(
    plan: PlanConfig,
    folder: ReportFolder,
    report: ReportConfig,
    context: ReportValidationContext,
): ReportValidationIssue[] {
    const issues: ReportValidationIssue[] = [];
    const reportLabel = trim(report.label);
    const folderLabel = trim(folder.label);
    const frequency = trim(report.frequency);
    const reportingPeriodType = normalizeReportingPeriodType(report.reportingPeriod?.type || "");

    if (!reportLabel) {
        issues.push(setupIssue("reportName.required", "Report name", "Enter a report name before saving this configuration.", "report-name"));
    }

    if (!folderLabel) {
        issues.push(setupIssue("folderName.required", "Folder name", "Enter a folder name for the selected report.", "folder-name"));
    }

    if (!frequency) {
        issues.push(setupIssue("frequency.required", "Run frequency", "Choose how often this report runs.", "report-frequency"));
    } else if (!isKnownOption(context.frequencyOptions, frequency)) {
        issues.push(setupIssue("frequency.unknown", "Run frequency", "The selected frequency is not available in the loaded lookup values.", "report-frequency"));
    }

    if (!trim(report.effectiveStart)) {
        issues.push(setupIssue("effectiveStart.required", "Effective start date", "Choose when this report configuration becomes active.", "effective-start"));
    }
    validateDateOrder(
        issues,
        trim(report.effectiveStart),
        trim(report.effectiveEnd),
        "effectiveDate",
        "Effective dates",
        "effective-start",
        "effective-end",
    );

    if (!reportingPeriodType) {
        issues.push(setupIssue("reportingPeriod.required", "Reporting period", "Choose the reporting period this report covers.", "reporting-period"));
    } else if (!isKnownOption(context.reportingPeriodOptions, reportingPeriodType)) {
        issues.push(setupIssue("reportingPeriod.unknown", "Reporting period", "The selected reporting period is not available in the loaded lookup values.", "reporting-period"));
    }

    if (isLastNReportingPeriod(reportingPeriodType) && validLastN(reportingPeriodType, report.reportingPeriod.lastN) === null) {
        const unit = reportingPeriodType === "LastNMonths" ? "months" : "days";
        issues.push(setupIssue("reportingPeriod.lastN", "Reporting period", `Enter the number of ${unit} - a whole number from 0 to ${lastNLimit(reportingPeriodType)}.`, "reporting-period-last-n"));
    }

    if (isCustomReportingPeriod(reportingPeriodType)) {
        const customStart = trim(report.reportingPeriod.customStart);
        const customEnd = trim(report.reportingPeriod.customEnd);
        if (!customStart) {
            issues.push(setupIssue("customPeriod.start.required", "Custom reporting period", "Choose the custom reporting period start date.", "reporting-period-custom-start"));
        }
        if (!customEnd) {
            issues.push(setupIssue("customPeriod.end.required", "Custom reporting period", "Choose the custom reporting period end date.", "reporting-period-custom-end"));
        }
        validateDateOrder(
            issues,
            customStart,
            customEnd,
            "customPeriod",
            "Custom reporting period",
            "reporting-period-custom-start",
            "reporting-period-custom-end",
        );
    }

    if (!report.models.length) {
        issues.push(setupIssue("dataModels.required", "Data models", "Add at least one data model before saving this report configuration.", "data-models"));
    }

    report.models.forEach((modelKey) => {
        const model = context.dataModels[modelKey];
        const label = model?.label || modelKey;
        const fieldKey = `model-based-on-${modelKey}`;
        if (!model) {
            issues.push(setupIssue(`dataModel.${modelKey}.missing`, "Data model", `"${label}" is no longer available in the loaded data model catalog.`, "data-models"));
            return;
        }
        if (!model.dataModelId) {
            issues.push(setupIssue(`dataModel.${modelKey}.id`, "Data model", `"${label}" has no backend DataModelId loaded yet.`, "data-models"));
        }
        if (!model.timestampColumns.length) {
            issues.push(setupIssue(`dataModel.${modelKey}.timestamp`, "Reporting period basis", `"${label}" has no timestamp columns configured for reporting-period filtering.`, fieldKey));
            return;
        }
        const selected = trim(report.modelBasedOn?.[modelKey]);
        if (!selected) return;
        const selectedColumn = model.timestampColumns.find((column) =>
            dataColumnSelectionKey(column) === selected || column.key === selected);
        if (!selectedColumn) {
            issues.push(setupIssue(`dataModel.${modelKey}.basis`, "Reporting period basis", `"${label}" uses a reporting-period basis column that is not available anymore.`, fieldKey));
        }
        periodBoundsIssues(report, modelKey).forEach((message, index) => {
            issues.push(setupIssue(`dataModel.${modelKey}.bounds.${index}`, "Reporting period basis", `"${label}": ${message}`, `model-period-bounds-${modelKey}`));
        });
    });

    const memberIds = report.globalFilters?.exclusions?.memberIds || [];
    const invoiceNumbers = report.globalFilters?.exclusions?.invoiceNumbers || [];
    if (hasBlankValue(memberIds)) {
        issues.push(setupIssue("memberExclusions.blank", "Member exclusions", "Remove blank member exclusion entries before saving.", "member-exclusions"));
    }
    const duplicateMemberIds = duplicateValues(memberIds);
    if (duplicateMemberIds.length) {
        issues.push(setupIssue("memberExclusions.duplicates", "Member exclusions", `Remove duplicate member exclusion entries: ${duplicateMemberIds.join(", ")}.`, "member-exclusions"));
    }
    if (hasBlankValue(invoiceNumbers)) {
        issues.push(setupIssue("invoiceExclusions.blank", "Invoice exclusions", "Remove blank invoice exclusion entries before saving.", "invoice-exclusions"));
    }
    const duplicateInvoiceNumbers = duplicateValues(invoiceNumbers);
    if (duplicateInvoiceNumbers.length) {
        issues.push(setupIssue("invoiceExclusions.duplicates", "Invoice exclusions", `Remove duplicate invoice exclusion entries: ${duplicateInvoiceNumbers.join(", ")}.`, "invoice-exclusions"));
    }

    validateCategorization(issues, report);
    validateCalculatedFields(issues, report);
    validateMetrics(issues, report);
    validateExcelLayouts(issues, report);

    return issues;
}
